import React, { useState } from 'react';
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Box } from '@mui/material';
import { Create, Gesture, Lens, LensOutlined, Stop, StopOutlined, TextFields } from '@mui/icons-material';
import chartingMenuSx from './ChartingMenu.styles';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../Chart.redux';
import { Colorful } from '@uiw/react-color';

const MenuMarkup = () => {
    const theme = useTheme();
    const sx = chartingMenuSx(theme);
    const [anchor, setAnchor] = useState(null),
        dispatch = useDispatch(),
        selected = useSelector((state) => state.chart.toolbar.selectedButton === 'markup'),
        tool = useSelector((state) => state.chart.toolbar.selectedTool),
        color = useSelector((state) => state.chart.toolbar.color),
        lineSize = useSelector((state) => state.chart.toolbar.lineSize),
        validTools = [
            'drawing',
            'text',
            'shapeRectangleOutlined',
            'shapeRectangleFilled',
            'shapeCircleOutlined',
            'shapeCircleFilled',
        ];

    const handleClose = () => setAnchor(null),
        handleOpen = (e) => {
            setAnchor(e.currentTarget);

            let shouldUpdate = false,
                update = {};

            if (!selected) {
                shouldUpdate = true;
                update.selectedButton = 'markup';
                update.selectedTool = 'drawing';
            }

            if (selected && validTools.includes(tool)) {
                shouldUpdate = false;
            }

            if (selected && !validTools.includes(tool)) {
                shouldUpdate = true;
                update.selectedTool = 'drawing';
            }

            if (shouldUpdate) {
                dispatch(actions.setToolbar(update));
            }
        },
        toggleTool = (name) => (e) => {
            dispatch(
                actions.setToolbar({
                    selectedTool: name,
                    selectedButton: 'markup',
                })
            );
            //setAnchor(null);
        },
        toolIcons = {
            drawing: <Create />,
            text: <TextFields />,
            shapeRectangleOutlined: <StopOutlined />,
            shapeRectangleFilled: <Stop />,
            shapeCircleOutlined: <LensOutlined />,
            shapeCircleFilled: <Lens />,
        },
        setLineSize = (size) => (e) => {
            dispatch(
                actions.setToolbar({
                    lineSize: size,
                })
            );
        },
        handleColorChange = (color, event) => {
            dispatch(
                actions.setToolbar({
                    color: color.hexa,
                })
            );
        };

    return (
        <>
            <Button
                variant={selected ? 'contained' : 'outlined'}
                color={selected ? 'primary' : undefined}
                sx={{
                    ...(selected ? { background: color } : {}),
                    ...sx.buttonMarginRight,
                    ...sx.buttonFloat,
                }}
                startIcon={toolIcons[tool] !== undefined ? toolIcons[tool] : <Gesture />}
                onClick={handleOpen}
            >
                Markup
            </Button>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            width: '300px',
                            maxWidth: '300px',
                        },
                    },
                }}
            >
                <MenuItem onClick={toggleTool('drawing')} sx={tool === 'drawing' ? sx.activeItem : undefined}>
                    <ListItemIcon>
                        <Create />
                    </ListItemIcon>
                    <ListItemText>Draw</ListItemText>
                </MenuItem>

                <MenuItem onClick={toggleTool('text')} sx={tool === 'text' ? sx.activeItem : undefined}>
                    <ListItemIcon>
                        <TextFields />
                    </ListItemIcon>
                    <ListItemText>Text Note</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={toggleTool('shapeRectangleOutlined')}
                    sx={tool === 'shapeRectangleOutlined' ? sx.activeItem : undefined}
                >
                    <ListItemIcon>
                        <StopOutlined />
                    </ListItemIcon>
                    <ListItemText>Rectangle (Outlined)</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={toggleTool('shapeRectangleFilled')}
                    sx={tool === 'shapeRectangleFilled' ? sx.activeItem : undefined}
                >
                    <ListItemIcon>
                        <Stop />
                    </ListItemIcon>
                    <ListItemText>Rectangle (Filled)</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={toggleTool('shapeCircleOutlined')}
                    sx={tool === 'shapeCircleOutlined' ? sx.activeItem : undefined}
                >
                    <ListItemIcon>
                        <LensOutlined />
                    </ListItemIcon>
                    <ListItemText>Circle (Outlined)</ListItemText>
                </MenuItem>

                <MenuItem
                    onClick={toggleTool('shapeCircleFilled')}
                    sx={tool === 'shapeCircleFilled' ? sx.activeItem : undefined}
                >
                    <ListItemIcon>
                        <Lens />
                    </ListItemIcon>
                    <ListItemText>Circle (Filled)</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={setLineSize(2)} sx={lineSize === 2 ? { ...sx.nested, ...sx.activeItem } : sx.nested}>
                    <Box sx={{ ...sx.lineExtraThin, background: color }} />
                </MenuItem>

                <MenuItem onClick={setLineSize(8)} sx={lineSize === 8 ? { ...sx.nested, ...sx.activeItem } : sx.nested}>
                    <Box sx={{ ...sx.lineThin, background: color }} />
                </MenuItem>

                <MenuItem
                    onClick={setLineSize(16)}
                    sx={lineSize === 16 ? { ...sx.nested, ...sx.activeItem } : sx.nested}
                >
                    <Box sx={{ ...sx.lineMedium, background: color }} />
                </MenuItem>

                <MenuItem
                    onClick={setLineSize(24)}
                    sx={lineSize === 24 ? { ...sx.nested, ...sx.activeItem } : sx.nested}
                >
                    <Box sx={{ ...sx.lineThick, background: color }} />
                </MenuItem>

                <MenuItem
                    onClick={setLineSize(36)}
                    sx={lineSize === 36 ? { ...sx.nested, ...sx.activeItem } : sx.nested}
                >
                    <Box sx={{ ...sx.lineExtraThick, background: color }} />
                </MenuItem>

                <Divider />

                <Colorful color={color} onChange={handleColorChange} style={{ width: '280px', marginLeft: '8px' }} />
            </Menu>
        </>
    );
};

export default MenuMarkup;
